import type { MemeScanCandidateLog, MemeScanCandidateStage, MemeScanLogDto } from "@/api/types";

interface MemeScanLogProps {
  scanLog: MemeScanLogDto | null;
}

const STAGE_STYLE: Record<MemeScanCandidateStage, { label: string; text: string; dot: string }> = {
  bought: { label: "Bought", text: "text-gold", dot: "bg-gold" },
  security_rejected: { label: "Security rejected", text: "text-sell", dot: "bg-sell" },
  sizing_rejected: { label: "Sizing rejected", text: "text-sell", dot: "bg-sell" },
  buy_failed: { label: "Buy failed", text: "text-sell", dot: "bg-sell" },
  already_open: { label: "Already open", text: "text-turquoise", dot: "bg-turquoise" },
  cooldown: { label: "Cooldown", text: "text-ink-dim", dot: "bg-ink-faint" },
  filtered_out: { label: "Filtered out", text: "text-ink-faint", dot: "bg-ink-faint" },
  not_screened: { label: "Not screened", text: "text-ink-faint", dot: "bg-ink-faint" },
};

const SOURCE_LABEL: Record<string, string> = {
  geckoterminal_new_pools: "new pool",
  geckoterminal_trending: "trending",
};

function timeLabel(timestampMs: number): string {
  const seconds = Math.max(0, Math.floor((Date.now() - timestampMs) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

function formatUsd(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

function CandidateRow({ candidate }: { candidate: MemeScanCandidateLog }) {
  const style = STAGE_STYLE[candidate.stage];
  return (
    <div className="rounded-lg border border-line bg-panel p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-5 shrink-0 font-mono text-[11px] tabular text-ink-faint">#{candidate.rank}</span>
          <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${style.dot}`} />
          <span className={`font-mono text-xs font-semibold uppercase tracking-wide ${style.text}`}>
            {style.label}
          </span>
          <span className="font-mono text-sm text-ink">{candidate.symbol}</span>
          <span className="rounded-full bg-void px-1.5 py-0.5 font-mono text-[10px] uppercase text-ink-faint">
            {SOURCE_LABEL[candidate.discoverySource] ?? candidate.discoverySource}
          </span>
        </div>
        {candidate.securityScore !== null && (
          <span className="shrink-0 font-mono text-[11px] tabular text-ink-dim">sec {candidate.securityScore}</span>
        )}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 font-mono text-[11px] text-ink-faint">
        <span>liq {formatUsd(candidate.liquidityUsd)}</span>
        <span>vol24h {formatUsd(candidate.volume24hUsd)}</span>
        {candidate.priceChange24hPercent !== null && (
          <span className={candidate.priceChange24hPercent >= 0 ? "text-profit" : "text-sell"}>
            {candidate.priceChange24hPercent >= 0 ? "+" : ""}
            {candidate.priceChange24hPercent.toFixed(1)}% 24h
          </span>
        )}
        {candidate.pairAgeMinutes !== null && <span>age {Math.round(candidate.pairAgeMinutes)}m</span>}
      </div>
      {candidate.rejectReasons.length > 0 && (
        <ul className="mt-1.5 space-y-0.5">
          {candidate.rejectReasons.map((reason, i) => (
            <li key={i} className="font-mono text-[11px] leading-relaxed text-ink-dim">
              — {reason}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function MemeScanLog({ scanLog }: MemeScanLogProps) {
  return (
    <section>
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-mono text-xs font-semibold uppercase tracking-wide text-ink-dim">Last Scan</h3>
        {scanLog && <span className="font-mono text-[11px] text-ink-faint">{timeLabel(scanLog.scannedAt)}</span>}
      </div>
      <p className="mt-1 font-mono text-[11px] text-ink-faint">
        Rule-based screening (liquidity/age filter → security check → position sizing) — the meme sleeve does not
        use the AI model that drives futures decisions.
      </p>

      {!scanLog ? (
        <div className="mt-3 rounded-xl border border-line bg-panel p-6 text-center font-mono text-xs text-ink-faint">
          No scans logged yet. The log fills in after the first scan cycle.
        </div>
      ) : (
        <>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div className="rounded-lg border border-line bg-panel px-3 py-2 text-center">
              <div className="font-mono text-sm font-semibold tabular text-ink">{scanLog.candidatesFound}</div>
              <div className="font-mono text-[10px] uppercase tracking-wide text-ink-faint">found</div>
            </div>
            <div className="rounded-lg border border-line bg-panel px-3 py-2 text-center">
              <div className="font-mono text-sm font-semibold tabular text-ink">{scanLog.candidatesAfterFilter}</div>
              <div className="font-mono text-[10px] uppercase tracking-wide text-ink-faint">passed filter</div>
            </div>
            <div className="rounded-lg border border-line bg-panel px-3 py-2 text-center">
              <div className="font-mono text-sm font-semibold tabular text-ink">{scanLog.screenedCount}</div>
              <div className="font-mono text-[10px] uppercase tracking-wide text-ink-faint">screened</div>
            </div>
            <div className="rounded-lg border border-line bg-panel px-3 py-2 text-center">
              <div className="font-mono text-sm font-semibold tabular text-gold">{scanLog.boughtCount}</div>
              <div className="font-mono text-[10px] uppercase tracking-wide text-ink-faint">bought</div>
            </div>
          </div>

          {scanLog.blockedReason && (
            <div className="mt-3 rounded-xl border border-gold-dim/50 bg-gold/5 p-3 font-mono text-xs text-gold">
              No candidates were screened this scan — {scanLog.blockedReason}.
            </div>
          )}

          {scanLog.candidates.length === 0 ? (
            <div className="mt-3 rounded-xl border border-line bg-panel p-6 text-center font-mono text-xs text-ink-faint">
              No candidates to show for this scan.
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              {scanLog.candidates.map((candidate) => (
                <CandidateRow key={candidate.id} candidate={candidate} />
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
